'use client';

import { useMemo, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { lösNavSökväg, plattaNavSökvägar } from '../nav-permissions';
import { sidebarsFörBrfAppOchKonsultApp } from '../nav-config-dashboard';

// ----------------------------------------------------------------------

/** Hela trädet, oberoende av användare och organisation — konstant. */
const ALLA_NAVSÖKVÄGAR = plattaNavSökvägar(sidebarsFörBrfAppOchKonsultApp);

type Props = {
  /** Det färdigfiltrerade trädet för den här användaren och organisationen. */
  navData: NavSectionProps['data'];
  /** Om organisationens menyinställningar hunnit läsas in. */
  klar: boolean;
  children: React.ReactNode;
};

/**
 * Skickar vidare till översikten när någon öppnar en sida vars menypost är
 * parkerad eller rollspärrad.
 *
 * Det här är en **bekvämlighetsspärr i klienten**, inget skydd. Data skyddas av
 * `@RequiresOrganisationRole` och radsäkerheten i backend. Spärren finns för att
 * en kund inte ska ramla in på en halvfärdig vy via en gammal bokmärkning eller
 * en gissad URL — inte för att hindra någon som vill in.
 *
 * Beslutet tas mot `navData`, samma träd sidomenyn ritas ur. Det är avsiktligt:
 * en egen lista över parkerade sidor hade glidit isär från menyn, och då hade
 * spärren börjat gälla sidor som är fullt synliga. Av samma skäl täcker den tre
 * fall med samma kod — parkerad `menyNyckel`, rollspärrad gren utan nyckel, och
 * de momsfiltrerade rutterna.
 *
 * Två lägen släpper alltid igenom:
 * - **Sökvägar trädet inte äger** (`lösNavSökväg` ger `undefined`): kontosidor,
 *   callbacks, delningslänkar och detaljrutter saknar menypost med flit.
 * - **Innan menyinställningarna är inlästa**, och medan trädet är tomt för att
 *   användaren ännu inte hämtats. Annars skulle en organisation som slagit på en
 *   parkerad modul kastas ut ur den varje gång sidan laddas.
 */
export function SynligaRutterGuard({ navData, klar, children }: Props) {
  const pathname = usePathname();
  const router = useRouter();

  const synligaSökvägar = useMemo(() => new Set(plattaNavSökvägar(navData)), [navData]);

  const parkerad = useMemo(() => {
    if (!klar || !pathname || synligaSökvägar.size === 0) {
      return false;
    }

    // Callbacken måste få läsa sitt svar innan någon omdirigerar — samma regel
    // som AuthGuard använder.
    if (pathname.includes('/callback')) {
      return false;
    }

    const ägare = lösNavSökväg(pathname, ALLA_NAVSÖKVÄGAR);

    // Översikten är målet för omdirigeringen; spärra den aldrig, annars loopar det.
    if (!ägare || ägare === paths.dashboard.root) {
      return false;
    }

    return !synligaSökvägar.has(ägare);
  }, [klar, pathname, synligaSökvägar]);

  useEffect(() => {
    if (parkerad) {
      router.replace(paths.dashboard.root);
    }
  }, [parkerad, router]);

  if (parkerad) {
    return null;
  }

  return <>{children}</>;
}
